export const PACKING_JPEG_QUALITY = 0.92;
export const PACKING_JPEG_MAX_BYTES = 3 * 1024 * 1024;

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to encode packing photo.'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', quality);
  });
}

function scaleCanvas(source: HTMLCanvasElement, scale: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(source.width * scale));
  canvas.height = Math.max(1, Math.round(source.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to encode packing photo.');
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** Copy the shutter frame so the review can show it while encoding continues. */
export function copyPackingFrame(source: HTMLCanvasElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to copy packing photo.');
  }
  ctx.drawImage(source, 0, 0);
  return canvas;
}

class PackingJpegCancelled extends Error {
  constructor() {
    super('Packing photo encode cancelled');
    this.name = 'PackingJpegCancelled';
  }
}

export function isPackingJpegCancelled(error: unknown): boolean {
  return error instanceof PackingJpegCancelled;
}

/**
 * One JPEG at quality 0.92. A file over 3 MB is scaled down once and encoded again.
 * `cancel` drops the result and skips the second encode. An encode already inside
 * `toBlob` runs to the end; the blob is then discarded.
 */
export interface PackingJpegEncode {
  readonly promise: Promise<Blob>;
  cancel(): void;
}

async function encodePackingJpegWithCancel(
  source: HTMLCanvasElement,
  isCancelled: () => boolean,
): Promise<Blob> {
  const first = await canvasToJpegBlob(source, PACKING_JPEG_QUALITY);
  if (isCancelled()) throw new PackingJpegCancelled();
  if (first.size <= PACKING_JPEG_MAX_BYTES) return first;
  const scale = 0.9 * Math.sqrt(PACKING_JPEG_MAX_BYTES / first.size);
  const second = await canvasToJpegBlob(scaleCanvas(source, scale), PACKING_JPEG_QUALITY);
  if (isCancelled()) throw new PackingJpegCancelled();
  return second;
}

export function startPackingJpegEncode(source: HTMLCanvasElement): PackingJpegEncode {
  let cancelled = false;
  return {
    promise: encodePackingJpegWithCancel(source, () => cancelled),
    cancel(): void {
      cancelled = true;
    },
  };
}
